// src/unAuthPages/AuthPage.js
// /signin and /signup: the same passwordless flow — Google (redirect) or an emailed sign-in link.
// States: the form, "check your inbox" after a link is sent, "confirm your email" when a link is
// opened on another device (no stored email); verifying a link shows the full-window AppLoader.

import React, { useState, useContext, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  getRedirectResult,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithRedirect,
} from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { auth, googleProvider } from '../firebase';
import { AuthContext } from '../AuthContext';
import AppLoader from '../components/appComponents/AppLoader';
import { takeReturnPath } from '../components/appComponents/returnPath';
import prefillGoogleName from './prefillGoogleName';
import ChannelMarquee from './site/ChannelMarquee';
import google_big_logo from '../assets/images/logo/google-big-logo.png';
import './authPage.css';

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// Set just before the Google redirect so the return trip shows a loader, not the form
const REDIRECT_FLAG = 'isLoading';

const COPY = {
  signin: {
    helmet: 'Bumpups | Login',
    title: 'Welcome back',
    subtitle: 'Log in to your Bumpups account.',
    emailCta: 'Email me a sign-in link',
    switchText: 'Don’t have an account?',
    switchTo: '/signup',
    switchLabel: 'Sign up',
  },
  signup: {
    helmet: 'Bumpups | Sign Up',
    title: 'Create your account',
    subtitle: 'Join thousands worldwide who chat with their videos using Bumpups.',
    emailCta: 'Get started free',
    switchText: 'Already have an account?',
    switchTo: '/signin',
    switchLabel: 'Log in',
  },
};

const SIGNUP_POINTS = [
  'Support for local and YouTube videos',
  'Deliver insights across all types of videos',
  'Get started with 60 minutes free',
];

const errorMessage = code => {
  switch (code) {
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return 'This sign-in link has expired or was already used. Request a new one below.';
    case 'auth/invalid-email':
      return 'That email doesn’t match the one the link was sent to.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a minute and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
};

export default function AuthPage({ mode }) {
  const copy = COPY[mode];
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);

  // An email link starts in "verifying" (or "confirm" without a stored email) from the very first
  // render, so the form never flashes before the loader
  const [view, setView] = useState(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return 'form';
    return localStorage.getItem('emailForSignIn') ? 'verifying' : 'confirm';
  }); // form | sent | confirm | verifying
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [resent, setResent] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(localStorage.getItem(REDIRECT_FLAG) === 'true');

  // The only place this page navigates: once a signed-in user exists (email link, Google, or
  // already signed in), go where they were headed — once, even if React re-runs the effect.
  const navigated = useRef(false);
  useEffect(() => {
    if (currentUser && !navigated.current) {
      navigated.current = true;
      navigate(takeReturnPath());
    }
  }, [currentUser, navigate]);

  const completeEmailLink = async (linkEmail) => {
    setView('verifying');
    setError('');
    try {
      await signInWithEmailLink(auth, linkEmail, window.location.href);
      localStorage.removeItem('emailForSignIn'); // the auth listener sets currentUser → effect above
    } catch (e) {
      setError(errorMessage(e.code));
      if (e.code === 'auth/invalid-email') {
        setView('confirm');
      } else {
        // A dead link: drop it from the URL so the form doesn't retry it
        window.history.replaceState(null, '', window.location.pathname);
        setView('form');
      }
    }
  };

  // Arriving from an email link, or back from the Google redirect. Runs once per page load even when
  // React re-runs effects (StrictMode), so a link is never redeemed twice and we never navigate twice.
  const arrivalHandled = useRef(false);
  useEffect(() => {
    if (arrivalHandled.current) return;
    arrivalHandled.current = true;
    if (isSignInWithEmailLink(auth, window.location.href)) {
      localStorage.setItem(REDIRECT_FLAG, 'false');
      setIsRedirecting(false);
      const stored = localStorage.getItem('emailForSignIn');
      if (stored) {
        setEmail(stored);
        completeEmailLink(stored);
      }
      return;
    }
    // "Sign up with Google" elsewhere on the site sends people here to start Google right away
    if (location.state?.google) {
      navigate(location.pathname, { replace: true, state: null });
      continueWithGoogle();
      return;
    }
    // A successful Google sign-in sets currentUser (→ effect above); a brand-new account also gets
    // its Google first name as its Name
    getRedirectResult(auth)
      .then(result => { if (result) prefillGoogleName(result).catch(() => {}); })
      .catch(e => setError(errorMessage(e.code)))
      .finally(() => {
        localStorage.setItem(REDIRECT_FLAG, 'false');
        setIsRedirecting(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendLink = async () => {
    if (!EMAIL_RE.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    setSending(true);
    setError('');
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: `${window.location.origin}/${mode}`,
        handleCodeInApp: true,
      });
      localStorage.setItem('emailForSignIn', email);
      setView('sent');
      return true;
    } catch (e) {
      setError(errorMessage(e.code));
      return false;
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    if (await sendLink()) {
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    }
  };

  const continueWithGoogle = async () => {
    setError('');
    localStorage.setItem(REDIRECT_FLAG, 'true');
    setIsRedirecting(true);
    try {
      await signInWithRedirect(auth, googleProvider);
    } catch (e) {
      localStorage.setItem(REDIRECT_FLAG, 'false');
      setIsRedirecting(false);
      setError(errorMessage(e.code));
    }
  };

  // Back from Google, or verifying an email link: full-window loader that hands off to the app's own
  if (isRedirecting || view === 'verifying') return <AppLoader label="Signing you in…" />;

  return (
    <div className="auth-page">
      <Helmet>
        <title>{copy.helmet}</title>
        <meta name="description" content="Get started with Bumpups and enjoy 60 free minutes. No credit card required." />
      </Helmet>

      <div className="auth-card">
        {view === 'form' && (
          <>
            <h1 className="auth-title">{copy.title}</h1>
            <p className="auth-subtitle">{copy.subtitle}</p>
            {mode === 'signup' && (
              <ul className="auth-points">
                {SIGNUP_POINTS.map(point => (
                  <li key={point}><FontAwesomeIcon icon={faCheck} className="auth-point-icon" />{point}</li>
                ))}
              </ul>
            )}

            <button type="button" className="auth-google" onClick={continueWithGoogle}>
              <img src={google_big_logo} alt="" className="auth-google-logo" />
              Continue with Google
            </button>

            <div className="auth-divider"><span>or</span></div>

            <form
              onSubmit={e => { e.preventDefault(); sendLink(); }}
              noValidate
            >
              <label className="auth-label" htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                type="email"
                className="auth-input"
                placeholder="you@example.com"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              {error && <p className="auth-error" role="alert">{error}</p>}
              <button type="submit" className="auth-primary" disabled={sending}>
                {sending ? 'Sending…' : copy.emailCta}
              </button>
            </form>

            <p className="auth-switch">
              {copy.switchText} <Link to={copy.switchTo}>{copy.switchLabel}</Link>
            </p>
            {mode === 'signup' && (
              <p className="auth-terms">
                By signing up, you agree to Bumpups’s <Link to="/terms-of-service">Terms of Service</Link> and{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            )}
          </>
        )}

        {view === 'sent' && (
          <div className="auth-state">
            <span className="auth-state-icon"><FontAwesomeIcon icon={faEnvelope} /></span>
            <h1 className="auth-title">Check your inbox</h1>
            <p className="auth-subtitle">
              We sent a sign-in link to <strong>{email}</strong>. Open it on this device to finish signing in.
            </p>
            {error && <p className="auth-error" role="alert">{error}</p>}
            <button type="button" className="auth-secondary" onClick={resend} disabled={sending}>
              {sending ? 'Sending…' : resent ? 'Link sent again' : 'Resend link'}
            </button>
            <button type="button" className="auth-text-btn" onClick={() => { setView('form'); setError(''); }}>
              Use a different email
            </button>
          </div>
        )}

        {view === 'confirm' && (
          <form
            className="auth-state"
            onSubmit={e => {
              e.preventDefault();
              if (!EMAIL_RE.test(email)) { setError('Please enter a valid email address.'); return; }
              completeEmailLink(email);
            }}
            noValidate
          >
            <span className="auth-state-icon"><FontAwesomeIcon icon={faEnvelope} /></span>
            <h1 className="auth-title">Confirm your email</h1>
            <p className="auth-subtitle">
              This link was opened on a different device or browser. Enter the email it was sent to.
            </p>
            <input
              type="email"
              className="auth-input"
              placeholder="you@example.com"
              autoComplete="email"
              aria-label="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            {error && <p className="auth-error" role="alert">{error}</p>}
            <button type="submit" className="auth-primary">Continue</button>
          </form>
        )}

      </div>

      {mode === 'signup' && view === 'form' && (
        <div className="auth-social">
          <ChannelMarquee caption="Bump AI will watch any video and deliver insights across all industries." />
        </div>
      )}
    </div>
  );
}
